/* eslint-disable max-len, object-curly-newline, react/jsx-one-expression-per-line */
import React, { useContext, useEffect, useState } from 'react';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import ContentCopy from '@mui/icons-material/ContentCopy';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import ConfirmDialog from '../../components/ConfirmDialog';
import { NotificationContext } from '../../contexts/Notification';
import { UserContext } from '../../contexts/User';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import { BaseUrl } from '../../config';

/*
 * Settings → API access.
 *
 * Lets an owner hand an outside system read-only access to this workspace's
 * approved time off, through keys issued here. Owners only — the tab is not
 * rendered for lower account types, matching the bar on the endpoint.
 *
 * A new key's plaintext lives only in `revealedKey` while the reveal dialog is
 * open. It is never written to storage and is dropped the moment the dialog
 * closes; the server keeps a hash, so there is no second chance to see it.
 */

// The feed URL; BaseUrl already carries the /api/ suffix.
const TIME_OFF_URL = `${BaseUrl}v1/time-off`;

const SAMPLE_RESPONSE = `{
  "generatedAt": "2026-10-20T09:00:00.000Z",
  "timezone": "UTC",
  "timeOff": [
    {
      "id": 1842,
      "email": "jane.doe@example.com",
      "start": "2026-10-21T04:00:00.000Z",
      "end": "2026-10-23T03:59:59.000Z",
      "allDay": true,
      "hours": 15
    },
    {
      "id": 1851,
      "email": "raj.patel@example.com",
      "start": "2026-10-22T13:00:00.000Z",
      "end": "2026-10-22T16:45:00.000Z",
      "allDay": false,
      "hours": 3.75
    }
  ],
  "holidays": [
    {
      "id": 31,
      "name": "Thanksgiving",
      "date": "2026-11-26",
      "emails": ["jane.doe@example.com", "raj.patel@example.com"]
    }
  ]
}`;

const formatDate = (value) => (value
  ? new Date(value).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
  : 'Never');

const keyFileName = (name) => `shoot-api-key-${(name || 'key').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'key'}.txt`;

const keyFileContents = ({ key, name, workspaceName }) => [
  'Shoot API key',
  '',
  `Name:       ${name}`,
  `Workspace:  ${workspaceName || ''}`,
  `Created:    ${new Date().toISOString()}`,
  '',
  `Key:        ${key}`,
  '',
  'Usage (keep this key server-side; never ship it to a browser or app):',
  `  curl -H "Authorization: Bearer ${key}" ${TIME_OFF_URL}`,
  '',
  'This key cannot be shown again. If it is lost or exposed, revoke it in',
  'Shoot under Settings → API Access and generate a new one.',
  '',
].join('\n');

const card = { padding: '16px 18px', border: '1px solid var(--dark-6, #E0E1E6)', borderRadius: 10 };
const h4 = { fontSize: 16, fontWeight: 600, color: 'var(--dark-1, #0E1E40)', margin: 0 };
const muted = { fontSize: 14, color: 'var(--dark-3, #546077)', margin: '4px 0 0', maxWidth: 680 };
const code = { fontSize: 13, background: 'var(--dark-9, #FBFBFC)', border: '1px solid var(--dark-6, #E0E1E6)', borderRadius: 6, padding: '4px 8px', color: 'var(--dark-2, #293855)', wordBreak: 'break-all' };

function ApiAccess() {
  const { displayNotification } = useContext(NotificationContext);
  const { activeWorkspace } = useContext(UserContext);
  const workspaceId = activeWorkspace?.id;

  const [enabled, setEnabled] = useState(false);
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [confirmEnable, setConfirmEnable] = useState(false);

  const [keyName, setKeyName] = useState('');
  const [creating, setCreating] = useState(false);
  // { key, name } while the one-time reveal is open; null otherwise.
  const [revealedKey, setRevealedKey] = useState(null);

  const [revoking, setRevoking] = useState(null);
  const [revokeBusy, setRevokeBusy] = useState(false);

  useEffect(() => {
    if (!workspaceId) return undefined;
    let active = true;
    (async () => {
      setLoading(true);
      const res = await sendRequest({ method: 'get', url: API.API_ACCESS.get(workspaceId) });
      if (!active) return;
      if (res.success) {
        setEnabled(!!res.data.enabled);
        setKeys(res.data.keys || []);
      } else {
        displayNotification({ message: res.data?.error || 'Failed to load API access settings', severity: 'error' });
      }
      setLoading(false);
    })();
    return () => { active = false; };
  }, [workspaceId]);

  const saveEnabled = async (next) => {
    setToggling(true);
    const res = await sendRequest({ method: 'put', url: API.API_ACCESS.update(workspaceId), data: { enabled: next } });
    setToggling(false);
    setConfirmEnable(false);
    if (!res.success) {
      displayNotification({ message: res.data?.error || 'Could not update API access', severity: 'error' });
      return;
    }
    setEnabled(!!res.data.enabled);
    displayNotification({
      message: res.data.enabled ? 'API access turned on' : 'API access turned off — every key is refused until it is turned back on',
      severity: 'success',
    });
  };

  // Turning on shares employee data, so it asks first. Turning off never does.
  const onToggle = () => {
    if (enabled) saveEnabled(false);
    else setConfirmEnable(true);
  };

  const createKey = async (event) => {
    event.preventDefault();
    const name = keyName.trim();
    if (!name || creating) return;
    setCreating(true);
    const res = await sendRequest({ method: 'post', url: API.API_ACCESS.createKey(workspaceId), data: { name } });
    setCreating(false);
    if (!res.success) {
      displayNotification({ message: res.data?.error || 'Could not generate a key', severity: 'error' });
      return;
    }
    setKeys((prev) => [res.data.apiKey, ...prev]);
    setKeyName('');
    setRevealedKey({ key: res.data.key, name: res.data.apiKey.name });
  };

  const copyText = async (text, what) => {
    try {
      await navigator.clipboard.writeText(text);
      displayNotification({ message: `${what} copied`, severity: 'success' });
    } catch (error) {
      displayNotification({ message: 'Could not copy — select the text and copy it manually', severity: 'info' });
    }
  };

  const downloadKey = () => {
    if (!revealedKey) return;
    const blob = new Blob([keyFileContents({ ...revealedKey, workspaceName: activeWorkspace?.name })], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = keyFileName(revealedKey.name);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const revoke = async () => {
    if (!revoking) return;
    setRevokeBusy(true);
    const res = await sendRequest({ method: 'delete', url: API.API_ACCESS.revokeKey(workspaceId, revoking.id) });
    setRevokeBusy(false);
    if (!res.success) {
      displayNotification({ message: res.data?.error || 'Could not revoke the key', severity: 'error' });
      return;
    }
    setKeys((prev) => prev.filter((key) => key.id !== revoking.id));
    displayNotification({ message: `Revoked "${revoking.name}"`, severity: 'success' });
    setRevoking(null);
  };

  return (
    <div style={{ padding: '20px 4px', fontFamily: 'Inter, sans-serif' }}>
      {/* Privacy notice — shown before the switch, on purpose. */}
      <div role="note" style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px', borderRadius: 8, marginBottom: 18, background: '#FFF8E6', border: '1px solid #F2D58A' }}>
        <WarningAmberOutlined style={{ fontSize: 20, color: '#9A6B00', flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: 14, color: 'var(--dark-2, #293855)' }}>
          <strong>Turning this on shares employee data with another system.</strong>
          {' '}Anyone holding a key can read the email address and approved time-off dates of every active member of this workspace, and the holidays assigned to them.
          Leave type, reason, policy and balances are never shared. Make sure your organization has approved this use, and that the receiving system keeps keys on its server.
        </div>
      </div>

      {/* Master switch */}
      <div style={{ ...card, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 22, background: enabled ? '#fff' : 'var(--dark-10, #FAFAFA)' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={h4}>API access</span>
            <span style={{ fontSize: 12, fontWeight: 600, borderRadius: 20, padding: '2px 8px', ...(enabled ? { background: '#E7F6E6', color: 'var(--green-1, #256D23)' } : { background: 'var(--dark-7, #ECECF0)', color: 'var(--dark-3, #546077)' }) }}>
              {enabled ? 'On' : 'Off'}
            </span>
          </div>
          <p style={muted}>
            Read-only, server-to-server access to approved time off. Nothing in Shoot can be changed through it. When off, every key below is refused.
          </p>
        </div>
        <Switch
          checked={enabled}
          disabled={loading || toggling}
          onChange={onToggle}
          color="primary"
          inputProps={{ 'aria-label': 'API access' }}
        />
      </div>

      {/* Keys */}
      <div style={{ marginBottom: 26 }}>
        <h4 style={h4}>API keys</h4>
        <p style={{ ...muted, marginBottom: 12 }}>
          Each key is shown once, when it is generated. Shoot keeps only a fingerprint of it, so a lost key cannot be recovered — revoke it and generate a new one.
        </p>

        <form onSubmit={createKey} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 14 }}>
          <TextField
            size="small"
            placeholder="Key name, e.g. POD dashboard"
            value={keyName}
            disabled={!enabled || creating}
            onChange={(event) => setKeyName(event.target.value)}
            inputProps={{ maxLength: 100, 'aria-label': 'Key name' }}
            sx={{ width: 300 }}
          />
          <Button
            type="submit"
            variant="contained"
            disabled={!enabled || creating || !keyName.trim()}
            sx={{ textTransform: 'none', backgroundColor: 'var(--primary, #213D69)', '&:hover': { backgroundColor: '#1a3156' } }}
          >
            {creating ? 'Generating…' : 'Generate key'}
          </Button>
          {!enabled && <span style={{ fontSize: 13, color: 'var(--dark-4, #7D8698)' }}>Turn on API access to generate a key.</span>}
        </form>

        <div style={{ border: '1px solid var(--dark-6, #E0E1E6)', borderRadius: 10, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: 'var(--dark-9, #FBFBFC)', textAlign: 'left', color: 'var(--dark-3, #546077)' }}>
                <th style={{ padding: '10px 14px', fontWeight: 600 }}>Name</th>
                <th style={{ padding: '10px 14px', fontWeight: 600 }}>Key</th>
                <th style={{ padding: '10px 14px', fontWeight: 600 }}>Created</th>
                <th style={{ padding: '10px 14px', fontWeight: 600 }}>Last used</th>
                <th style={{ padding: '10px 14px' }} aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {!loading && keys.length === 0 && (
                <tr><td colSpan={5} style={{ padding: '18px 14px', color: 'var(--dark-4, #7D8698)' }}>No active keys.</td></tr>
              )}
              {keys.map((key) => (
                <tr key={key.id} style={{ borderTop: '1px solid var(--dark-6, #E0E1E6)' }}>
                  <td style={{ padding: '10px 14px', fontWeight: 600, color: 'var(--dark-1, #0E1E40)' }}>{key.name}</td>
                  <td style={{ padding: '10px 14px' }}><code style={code}>{key.keyPrefix}…</code></td>
                  <td style={{ padding: '10px 14px', color: 'var(--dark-2, #293855)' }}>
                    {formatDate(key.createdAt)}
                    {key.createdBy && <span style={{ display: 'block', fontSize: 12, color: 'var(--dark-4, #7D8698)' }}>by {key.createdBy}</span>}
                  </td>
                  <td style={{ padding: '10px 14px', color: 'var(--dark-2, #293855)' }}>{formatDate(key.lastUsedAt)}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                    <Button size="small" onClick={() => setRevoking(key)} sx={{ textTransform: 'none', color: '#CC4525' }}>Revoke</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Docs */}
      <div style={card}>
        <h4 style={h4}>Endpoint</h4>
        <p style={muted}>Approved time off that is happening now or booked ahead, plus holidays in the next 365 days. Every time is UTC; holiday dates are calendar dates.</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 10 }}>
          <code style={code}>GET {TIME_OFF_URL}</code>
          <Button size="small" startIcon={<ContentCopy style={{ fontSize: 15 }} />} onClick={() => copyText(TIME_OFF_URL, 'Endpoint URL')} sx={{ textTransform: 'none', color: 'var(--primary, #213D69)' }}>Copy</Button>
        </div>
        <p style={{ ...muted, marginTop: 12 }}>
          Send the key in a header: <code style={code}>Authorization: Bearer &lt;key&gt;</code>. Keys in the URL are ignored.
          Requests are limited to 60 per minute per key. Responses: <strong>401</strong> invalid or revoked key, <strong>403</strong> API access turned off, <strong>429</strong> rate limit (see <code style={code}>Retry-After</code>).
        </p>
        <ul style={{ fontSize: 14, color: 'var(--dark-2, #293855)', margin: '10px 0 0', paddingLeft: 18 }}>
          <li><code style={code}>timeOff[]</code> — one entry per approved request: <code style={code}>email</code>, <code style={code}>start</code>/<code style={code}>end</code> (UTC instants), <code style={code}>allDay</code>, and <code style={code}>hours</code>. Partial days are included with their exact times.</li>
          <li style={{ marginTop: 6 }}><code style={code}>holidays[]</code> — <code style={code}>name</code>, <code style={code}>date</code> (YYYY-MM-DD), and the <code style={code}>emails</code> of members it applies to.</li>
        </ul>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 14 }}>
          <InfoOutlined style={{ fontSize: 16, color: 'var(--primary, #213D69)' }} />
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--dark-2, #293855)' }}>Sample response</span>
        </div>
        <pre style={{ ...code, display: 'block', marginTop: 6, padding: 12, overflowX: 'auto', whiteSpace: 'pre', wordBreak: 'normal' }}>{SAMPLE_RESPONSE}</pre>
      </div>

      {confirmEnable && (
        <ConfirmDialog
          tone="brand"
          icon={<WarningAmberOutlined style={{ fontSize: 26 }} />}
          title="Turn on API access?"
          description="Keys issued from this workspace will be able to read the email address and approved time-off dates of every active member, and the holidays assigned to them. Only turn this on if your organization has approved sharing this data with the receiving system."
          confirmLabel="Turn on"
          confirming={toggling}
          onConfirm={() => saveEnabled(true)}
          onCancel={() => setConfirmEnable(false)}
        />
      )}

      {revoking && (
        <ConfirmDialog
          tone="danger"
          title={`Revoke "${revoking.name}"?`}
          description="Any system using this key will be refused from its next request. This cannot be undone — you would need to generate a new key."
          confirmLabel="Revoke"
          confirming={revokeBusy}
          onConfirm={revoke}
          onCancel={() => setRevoking(null)}
        />
      )}

      {/* One-time reveal. Closing it drops the key from memory. */}
      <Modal open={!!revealedKey} onClose={() => {}} className="flex-row align-c jstfy-c" aria-labelledby="api-key-reveal-title">
        <Box style={{ width: 560, maxWidth: 'calc(100% - 32px)', padding: 24, background: '#fff', borderRadius: 12, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <h5 id="api-key-reveal-title" className="text-lg font-semibold leading-none text-ink-900 mb-0">Your new API key</h5>
          <p style={{ ...muted, margin: 0 }}>
            Copy or download it now. <strong>This is the only time it will be shown.</strong> Store it in the receiving system&apos;s server-side secrets, never in a browser or app.
          </p>
          <code data-testid="revealed-key" style={{ ...code, display: 'block', padding: '10px 12px', fontSize: 14 }}>{revealedKey?.key}</code>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <Button variant="outlined" startIcon={<ContentCopy style={{ fontSize: 16 }} />} onClick={() => copyText(revealedKey.key, 'API key')} sx={{ textTransform: 'none' }}>Copy key</Button>
            <Button variant="outlined" startIcon={<FileDownloadOutlined style={{ fontSize: 18 }} />} onClick={downloadKey} sx={{ textTransform: 'none' }}>Download .txt</Button>
            <span style={{ flex: 1 }} />
            <Button
              variant="contained"
              onClick={() => setRevealedKey(null)}
              sx={{ textTransform: 'none', backgroundColor: 'var(--primary, #213D69)', '&:hover': { backgroundColor: '#1a3156' } }}
            >
              I&apos;ve saved it
            </Button>
          </div>
        </Box>
      </Modal>
    </div>
  );
}

export default ApiAccess;
